import { Box, Typography } from '@mui/material';
import React, { useId, useRef } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../core-components/theme/components/Buttons';
import { InlineAlert } from '../../../../core-components/theme/components/InlineAlert';
import { emptyRegionSx } from '../../../../core-components/theme/components/Screen';
import { StickyActionBar } from '../../../../core-components/theme/components/StickyActionBar';
import { useFocusOnMount } from '../../../../utilities/customHooks/useFocusOnMount';
import type { NewPasswordField } from '../../domain/newPasswordRules';
import { useNewPassword } from '../../hooks/password/useNewPassword';
import { usePasswordRules } from '../../hooks/password/usePasswordRules';
import type { PasswordGrant } from '../../types/domain.types';
import { AuthPanelHeading, AuthSubtitle, authContentSx, authPanelSx } from '../AuthPanel';
import { newPasswordFailureText } from '../refusalText';

import { NewPasswordFields } from './NewPasswordFields';
import type { NewPasswordRefs } from './NewPasswordFields';

export interface NewPasswordStepProps {
  readonly grant: PasswordGrant;
  readonly onFinished: () => void;
}

const focusField = (ref: RefObject<HTMLInputElement>): void => ref.current?.focus();

const NewPasswordHeading: React.FC<{ readonly subtitleId: string; readonly titleRef?: RefObject<HTMLHeadingElement> }> = ({ subtitleId, titleRef }) => {
  const { t } = useTranslation();
  return (
    <AuthPanelHeading title={t('passcodeAuth.newPassword.title', 'Set a new password')} titleRef={titleRef} titleDescribedBy={subtitleId}>
      <AuthSubtitle id={subtitleId}>{t('passcodeAuth.newPassword.subtitle', 'Optional. You can always log in with a code.')}</AuthSubtitle>
    </AuthPanelHeading>
  );
};

const SkipDock: React.FC<{ readonly onSkip: () => void }> = ({ onSkip }) => {
  const { t } = useTranslation();
  return (
    <StickyActionBar
      layout="dock"
      announce={false}
      tertiary={
        <Button type="button" intent="tertiary" onClick={onSkip}>
          {t('passcodeAuth.newPassword.skip', 'Skip for now')}
        </Button>
      }
    />
  );
};

const RulesLoading: React.FC<{ readonly onSkip: () => void }> = ({ onSkip }) => {
  const { t } = useTranslation();
  const subtitleId = useId();
  return (
    <Box sx={authPanelSx}>
      <Box sx={authContentSx}>
        <NewPasswordHeading subtitleId={subtitleId} />
        <Typography role="status" variant="body" component="p">
          {t('common.loading', 'Loading...')}
        </Typography>
      </Box>
      <SkipDock onSkip={onSkip} />
    </Box>
  );
};

interface RulesFailedProps {
  readonly retrying: boolean;
  readonly onRetry: () => void;
  readonly onSkip: () => void;
}

const RulesFailed: React.FC<RulesFailedProps> = ({ retrying, onRetry, onSkip }) => {
  const { t } = useTranslation();
  const subtitleId = useId();
  return (
    <Box sx={authPanelSx}>
      <Box sx={authContentSx}>
        <NewPasswordHeading subtitleId={subtitleId} />
        <InlineAlert
          severity="error"
          role="alert"
          message={t('passcodeAuth.newPassword.rulesFailed', 'Couldn’t load the password rules. Try again, or skip and set a password later from your profile.')}
          action={
            <Button type="button" intent="tertiary" busy={retrying} onClick={onRetry}>
              {t('common.tryAgain', 'Try again')}
            </Button>
          }
        />
      </Box>
      <SkipDock onSkip={onSkip} />
    </Box>
  );
};

const NewPasswordForm: React.FC<NewPasswordStepProps & { readonly minLength: number }> = ({ grant, minLength, onFinished }) => {
  const { t } = useTranslation();
  const subtitleId = useId();
  const ruleId = useId();
  const titleRef = useFocusOnMount<HTMLHeadingElement>(true);
  const refs: NewPasswordRefs = { next: useRef<HTMLInputElement>(null), confirm: useRef<HTMLInputElement>(null) };
  const step = useNewPassword({ grant, minLength, onFinished });
  const save = (): void => {
    const failing: NewPasswordField | null = step.save();
    if (failing !== null) focusField(refs[failing]);
  };

  return (
    <Box
      component="form"
      noValidate
      sx={authPanelSx}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        save();
      }}
    >
      <Box sx={authContentSx}>
        <NewPasswordHeading subtitleId={subtitleId} titleRef={titleRef} />
        {step.failure !== null && <InlineAlert severity="error" role="alert" message={newPasswordFailureText(t, step.failure)} />}
        <NewPasswordFields
          values={step.values}
          errors={step.errors}
          rule={step.rule}
          minLength={minLength}
          refs={refs}
          ruleId={ruleId}
          readOnly={step.saving}
          onChange={step.setValue}
          onBlur={step.markTouched}
          onSubmit={save}
        />
        <Box role="status" sx={emptyRegionSx}>
          {step.waitingForNetwork && (
            <InlineAlert severity="info" message={t('passcodeAuth.newPassword.waiting', 'Waiting for the connection. Your password will save once it’s back.')} />
          )}
        </Box>
      </Box>
      <StickyActionBar
        layout="dock"
        announce={false}
        tertiary={
          <Button type="button" intent="tertiary" aria-disabled={step.saving || undefined} onClick={step.skip}>
            {t('passcodeAuth.newPassword.skip', 'Skip for now')}
          </Button>
        }
        primary={
          <Button type="submit" intent="primary" busy={step.saving} aria-describedby={ruleId}>
            {t('passcodeAuth.newPassword.save', 'Save password')}
          </Button>
        }
      />
    </Box>
  );
};

export const NewPasswordStep: React.FC<NewPasswordStepProps> = ({ grant, onFinished }) => {
  const rules = usePasswordRules();
  switch (rules.kind) {
    case 'ready':
      return <NewPasswordForm grant={grant} minLength={rules.minLength} onFinished={onFinished} />;
    case 'failed':
      return <RulesFailed retrying={rules.retrying} onRetry={rules.retry} onSkip={onFinished} />;
    case 'loading':
      return <RulesLoading onSkip={onFinished} />;
  }
};
